import React, { Component } from 'react';
import axios from 'axios';
import config from '../config';
import { Grid, CircularProgress, styled, keyframes } from '@mui/material';
import CopyToClipboard from 'react-copy-to-clipboard';
import { ArrowBack, ContentCopy, Refresh } from '@mui/icons-material';
import { withTheme } from '../mui-styles-bridge';
import withNavigate from '../utils/withNavigate';
import {
  PrimaryButton,
  SecondaryButton,
  NeutralButton,
} from './component/common';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const checkmarkDraw = keyframes`
  0% {
    stroke-dashoffset: 50;
  }
  100% {
    stroke-dashoffset: 0;
  }
`;

// Styled components
const PageContainer = styled('div')({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background:
    'linear-gradient(180deg, var(--color-bg-subtle, #fafbfc) 0%, var(--color-bg-paper, #ffffff) 100%)',
  animation: `${fadeIn} 0.3s ease-out`,
});

const ContentCard = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '48px',
  maxWidth: '480px',
  width: '90%',
  animation: `${slideUp} 0.5s ease-out`,
  animationDelay: '0.1s',
  animationFillMode: 'backwards',
});

const SuccessCircle = styled('div')({
  width: '80px',
  height: '80px',
  borderRadius: '8px',
  backgroundColor: 'var(--status-green-bg, #ecfdf5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '24px',
  '& svg': {
    width: '40px',
    height: '40px',
    stroke: 'var(--status-green-color, #059669)',
    strokeWidth: 3,
    fill: 'none',
    '& polyline': {
      strokeDasharray: 50,
      strokeDashoffset: 0,
      animation: `${checkmarkDraw} 0.5s ease-out`,
      animationDelay: '0.3s',
      animationFillMode: 'backwards',
    },
  },
});

const Title = styled('h2')({
  fontSize: '24px',
  fontWeight: 600,
  color: 'var(--color-text-primary, #18181b)',
  margin: '0 0 8px 0',
  textAlign: 'center',
});

const Subtitle = styled('p')({
  fontSize: '15px',
  color: 'var(--color-text-secondary, #71717a)',
  margin: '0 0 32px 0',
  textAlign: 'center',
  lineHeight: 1.5,
  animation: `${pulse} 2s ease-in-out infinite`,
  animationDelay: '1s',
});

const LoadingIndicator = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '32px',
});

const LoadingText = styled('span')({
  fontSize: '14px',
  color: 'var(--color-text-secondary, #71717a)',
});

const ButtonGroup = styled('div')({
  display: 'flex',
  gap: '12px',
  flexWrap: 'wrap',
  justifyContent: 'center',
});

// Timeout state styles
const TimeoutCard = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '32px',
  backgroundColor: 'var(--status-amber-bg, #fff3cd)',
  borderRadius: '16px',
  border: '1px solid var(--status-amber-border, #ffeaa7)',
  maxWidth: '400px',
  width: '90%',
  marginBottom: '24px',
});

const TimeoutIcon = styled('div')({
  width: '56px',
  height: '56px',
  borderRadius: '8px',
  backgroundColor: 'var(--status-amber-bg, #fffbeb)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '16px',
  fontSize: '28px',
});

const TimeoutTitle = styled('h3')({
  fontSize: '18px',
  fontWeight: 600,
  color: 'var(--status-amber-color, #856404)',
  margin: '0 0 8px 0',
  textAlign: 'center',
});

const TimeoutText = styled('p')({
  fontSize: '14px',
  color: 'var(--color-text-secondary, #71717a)',
  margin: '0',
  textAlign: 'center',
  lineHeight: 1.5,
});

const CopiedToast = styled('div')(({ show }) => ({
  position: 'fixed',
  bottom: '24px',
  left: '50%',
  transform: `translateX(-50%) ${show ? 'translateY(0)' : 'translateY(20px)'}`,
  backgroundColor: 'var(--color-text-primary, #18181b)',
  color: 'var(--color-bg-paper, #fff)',
  padding: '12px 24px',
  borderRadius: '8px',
  fontSize: '14px',
  opacity: show ? 1 : 0,
  transition: 'all 0.3s ease',
  pointerEvents: 'none',
}));

// Checkmark SVG component
const CheckmarkIcon = () => (
  <svg viewBox="0 0 24 24">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

class FormWaiting extends Component {
  state = {
    poolCount: 0,
    showCopiedToast: false,
  };

  constructor(props) {
    super(props);
  }

  queryNextForm = async () => {
    const controller = this.props.isTest
      ? '/find_next_test_form?'
      : '/find_next_form?';

    await axios(
      config.API_ADDRESS +
        controller +
        'form_id=' +
        this.props.match.params.form_id +
        '&' +
        'session_id=' +
        this.props.match.params.session_id +
        '&' +
        'project_session_id=' +
        this.props.match.params.project_session_id,
      {
        method: 'get',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }
    ).then((response) => {
      const formRoute = this.props.isTest ? 'form/form_test' : 'form';

      if (
        response.data &&
        response.data.data &&
        response.data.data.formId &&
        response.data.data.formSessionId
      ) {
        if (this.props.isTest) {
          this.props.history.push(
            `/${formRoute}/${response.data.data.formId}/${this.props.match.params.test_id}/${response.data.data.formSessionId}`
          );
        } else {
          this.props.history.push(
            `/${formRoute}/${response.data.data.formId}/${response.data.data.formSessionId}`
          );
        }
      }
      this.setState({ poolCount: this.state.poolCount + 1 });
    });
  };

  async componentDidMount() {
    // Fire the first poll immediately instead of waiting for the interval
    this.queryNextForm();
    this.startPolling();
  }

  startPolling() {
    // Poll aggressively at first (500ms), then slow down
    const getInterval = (count) => {
      if (count < 5) return 500;   // First 2.5s: every 500ms
      if (count < 15) return 1000; // Next 10s: every 1s
      return 2000;                 // After that: every 2s
    };

    const scheduleNext = () => {
      if (this.state.poolCount > 60) {
        return; // Give up after ~60 attempts
      }
      this.timer = setTimeout(async () => {
        await this.queryNextForm();
        scheduleNext();
      }, getInterval(this.state.poolCount));
    };

    scheduleNext();
  }

  componentDidUpdate() {
    // No-op: timeout-based polling self-terminates
  }

  componentWillUnmount() {
    clearTimeout(this.timer);
    this.timer = null;
  }

  handleBack = () => {
    let formSessionURL = `/form/${this.props.match.params.form_id}/${this.props.match.params.session_id}`;

    if (this.props.isTest) {
      const testId = this.props.match.params.test_id;
      formSessionURL = `/form/form_test/${this.props.match.params.form_id}/${testId}/${this.props.match.params.session_id}`;
    }

    this.props.history.push(formSessionURL);
  };

  handleCopy = () => {
    this.setState({ showCopiedToast: true });
    setTimeout(() => {
      this.setState({ showCopiedToast: false });
    }, 2000);
  };

  handleRetry = () => {
    window.location.reload();
  };

  render() {
    const { poolCount, showCopiedToast } = this.state;
    const isLoading = poolCount <= 60;

    return (
      <PageContainer>
        <ContentCard>
          <SuccessCircle>
            <CheckmarkIcon />
          </SuccessCircle>

          <Title>Response submitted</Title>

          {isLoading ? (
            <>
              <Subtitle>Loading next section...</Subtitle>
              <LoadingIndicator>
                <CircularProgress
                  size={24}
                  thickness={4}
                  sx={{ color: 'var(--color-primary, #18181b)' }}
                />
              </LoadingIndicator>
            </>
          ) : (
            <>
              <TimeoutCard>
                <TimeoutIcon>⏸</TimeoutIcon>
                <TimeoutTitle>Taking longer than expected</TimeoutTitle>
                <TimeoutText>
                  The next section is still being prepared. You can retry, go
                  back, or save this link to continue later.
                </TimeoutText>
              </TimeoutCard>

              <ButtonGroup>
                <NeutralButton
                  startIcon={<ArrowBack />}
                  onClick={this.handleBack}
                >
                  Back
                </NeutralButton>

                <SecondaryButton
                  startIcon={<Refresh />}
                  onClick={this.handleRetry}
                >
                  Retry
                </SecondaryButton>

                <CopyToClipboard
                  text={window.location.href}
                  onCopy={this.handleCopy}
                >
                  <PrimaryButton startIcon={<ContentCopy />}>
                    Copy Link
                  </PrimaryButton>
                </CopyToClipboard>
              </ButtonGroup>
            </>
          )}
        </ContentCard>

        <CopiedToast show={showCopiedToast}>
          Link copied to clipboard
        </CopiedToast>
      </PageContainer>
    );
  }
}

export default withTheme(withNavigate(FormWaiting));
